<template>
  <div>
    <b-container>
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(next)" autocomplete="off">
          <b-card>
            <template #header>
              <h6 class="mb-0">Declaración de Estado de Salud del Riesgo</h6>
            </template>

            <p class="text-muted small mb-3">
              Marque Sí o No. Si alguna respuesta es afirmativa, indique
              el detalle en la grilla (máximo 4 padecimientos).
            </p>

            <b-row>
              <b-col md="12" class="mb-3">
                <label class="d-block font-weight-bold">1. ¿Padezco o he
                  padecido alguna de las siguientes enfermedades?</label>
                <p class="small text-secondary">
                  Cardiovasculares, cerebrovasculares, renales, pulmonares,
                  neurológicas, gastrointestinales, diabetes, de tiroides, VIH/Sida,
                  Presión Arterial Alta, Lupus, Cáncer, tumores,
                  drogadicción o alcoholismo.
                </p>
                <RadioComponent :form="form" name="declaracionEnfermedades" :options="control.TIPOS_SINO"
                  rules="required" />
              </b-col>
            </b-row>

            <b-row>
              <b-col md="12" class="mb-3">
                <label class="d-block font-weight-bold">2. ¿Padezco alguna incapacidad,
                  defecto físico o mental o cualquier otra incapacidad permanente
                  o parcial diferente a los mencionados en el numeral anterior?</label>
                <RadioComponent :form="form" name="declaracionIncapacidad" :options="control.TIPOS_SINO"
                  rules="required" />
              </b-col>
            </b-row>

            <div v-if="muestraGrillaPadecimientos">
              <p v-if="ambasPreguntasSi" class="small text-muted mb-2">
                Indique si cada fila corresponde a la pregunta 1 o 2.
              </p>
              <div class="table-responsive">
                <table class="table-one tabla-padecimientos">
                  <thead>
                    <tr>
                      <th :class="{ 'pregunta-col': true, 'd-none': !ambasPreguntasSi }">
                        Corresponde a pregunta
                      </th>
                      <th>Tratamiento / detalle</th>
                      <th>Médico / hospital</th>
                      <th>Fecha inicio</th>
                      <th>Fecha fin</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in form.padecimientos" :key="index">
                      <td style="display: none">
                        <span>Padecimiento {{ index + 1 }}</span>
                      </td>
                      <td :class="{ 'pregunta-col': true, 'd-none': !ambasPreguntasSi }" style="width: 13%">
                        <b-form-select v-model="item.pregunta" :options="opcionesPregunta"
                          :state="getValidationPregunta(index)" />
                      </td>
                      <td style="width: 30%">
                        <b-form-input v-model="item.tratamiento" placeholder="Padecimiento / Tratamiento"
                          :state="getValidationTratamiento(index)" />
                      </td>
                      <td style="width: 30%">
                        <b-form-input v-model="item.medicoHospital" placeholder="Médico / hospital"
                          :state="getValidationMedicoHospital(index)" />
                      </td>
                      <td style="width: 10%">
                        <b-form-input v-model="item.fechaInicio" name="fechaInicio" 
                                      type="date" :state="getValidationFechaInicio(index)"/>        
                      </td>
                      <td style="width: 10%">
                        <b-form-input v-model="item.fechaFin" name="fechaFin" 
                          type="date" :state="getValidationFechaFin(index)"/> 
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <b-alert v-if="form.padecimientos && form.padecimientos.length > 4" show variant="danger">
              No puede haber más de 4 padecimientos en total.
            </b-alert>

            <b-row align-h="end" class="mt-4">
              <b-col md="4" cols="4" style="display: flex; justify-content: flex-end">
                <b-button @click="previous" variant="secondary">Anterior</b-button>
                <b-button type="submit" variant="primary">Siguiente</b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import RadioComponent from "@/components/controls/RadioComponent.vue";
import { TIPOS_SINO } from "@/data/colombia/vida/Selects";   
import { isValidDate } from "@/helpers/dateHelper";

const PADECIMIENTO_VACIO = () => ({
  pregunta: "",
  tratamiento: "",
  medicoHospital: "",
  fechaInicio: "",
  fechaFin: "",
});

const OPCION_PREGUNTA_1 = { value: "1", text: "1 (Enfermedades)" };
const OPCION_PREGUNTA_2 = { value: "2", text: "2 (Incapacidad)" };
const CANTIDAD_FILAS_DEFAULT = 4;

export default {
  name: "FormColombia03",
  components: { RadioComponent },
  data() {
    return {
      form: {},
      control: { TIPOS_SINO },
    };
  },
  computed: {
    muestraGrillaPadecimientos() {
      return this.form.declaracionEnfermedades === "S" ||
        this.form.declaracionIncapacidad === "S";
    },
    ambasPreguntasSi() {
      return this.form.declaracionEnfermedades === "S" &&
        this.form.declaracionIncapacidad === "S";
    },
    /** Opciones del combo "Corresponde a pregunta": solo las preguntas que están en Sí */
    opcionesPregunta() {
      const p1 = this.form.declaracionEnfermedades === "S";
      const p2 = this.form.declaracionIncapacidad === "S";
      if (p1 && p2) return [OPCION_PREGUNTA_1, OPCION_PREGUNTA_2];
      if (p1) return [OPCION_PREGUNTA_1];
      if (p2) return [OPCION_PREGUNTA_2];
      return [];
    }
  },
  watch: {
    "form.declaracionEnfermedades"() {
      this.asegurarGrillaCuatroFilas();
    },
    "form.declaracionIncapacidad"() {
      this.asegurarGrillaCuatroFilas();
    },
  },
  mounted() {
    this.$store.dispatch("app/loading", true);
    const { form } = this.$store.state.wizard;

    if (form === undefined || form === null || form === "") {
      this.$router.push("/form/colombia/vida/");
      return;
    }
    this.form = form;
    if (!Array.isArray(this.form.padecimientos)) {
      this.form.padecimientos = [];
    }
    this.asegurarGrillaCuatroFilas();
    this.$store.dispatch("app/loading", false);
  },

  methods: {
    /** Deja la grilla con 4 filas por defecto; si solo una pregunta */
    /** Sí, fija esa pregunta en todas */
    asegurarGrillaCuatroFilas() {
      if (!this.muestraGrillaPadecimientos) return;
      if (!this.form.padecimientos) this.form.padecimientos = [];
      const list = this.form.padecimientos;
      let preguntaDefault = "";
      if (this.form.declaracionEnfermedades === "S" &&
        this.form.declaracionIncapacidad !== "S") {
        preguntaDefault = "1";
      } else if (this.form.declaracionIncapacidad === "S" &&
        this.form.declaracionEnfermedades !== "S") {
        preguntaDefault = "2";
      }
      while (list.length < CANTIDAD_FILAS_DEFAULT) {
        list.push(PADECIMIENTO_VACIO(preguntaDefault));
      }

      if (preguntaDefault && list.length > 0) {
        list.forEach((p) => {
          p.pregunta = preguntaDefault;
        });
      }
    },
    previous() {
      this.$store
        .dispatch("wizard/save", { index: 2, form: this.form })
        .then(() => this.$router.push("/form/colombia-vida-2.html"));
    },
    getValidationPregunta(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].pregunta != "";
      if (state && this.form.padecimientos[index].pregunta == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationTratamiento(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].tratamiento != "";
      if (state && this.form.padecimientos[index].tratamiento == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationMedicoHospital(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].medicoHospital != "";
      if (state && this.form.padecimientos[index].medicoHospital == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationFechaInicio(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].fechaInicio != ""  
                  && isValidDate(this.form.padecimientos[index].fechaInicio);
      if (state && this.form.padecimientos[index].fechaInicio == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationFechaFin(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].fechaFin != ""
                  && isValidDate(this.form.padecimientos[index].fechaFin)
                  && this.form.padecimientos[index].fechaInicio < this.form.padecimientos[index].fechaFin;
      
                  if (state && this.form.padecimientos[index].fechaFin == "") {
        return null;
      } else {
        return state;
      }
    },
    next() {
      const list = this.form.padecimientos || [];
      if (this.form.declaracionEnfermedades === "S") {
        const conPregunta1 = list.filter((p) => p.pregunta === "1");
        const algunoP1ConDatos = conPregunta1.some((p) => this.padecimientoConDatos(p));
        if (!algunoP1ConDatos) {
          this.$bvToast.toast(
            "Si respondió Sí a la pregunta 1, indique al menos un detalle (p. ej. tratamiento) " +
            "para una fila correspondiente a la pregunta 1.",
            { title: "Validación", variant: "warning", solid: true }
          );
          return;
        }
      }
      if (this.form.declaracionIncapacidad === "S") {
        const conPregunta2 = list.filter((p) => p.pregunta === "2");
        const algunoP2ConDatos = conPregunta2.some((p) => this.padecimientoConDatos(p));
        // True si hay al menos un padecimiento con datos para la pregunta 2
        if (!algunoP2ConDatos) {
          this.$bvToast.toast(
            "Si respondió Sí a la pregunta 2, indique al menos un detalle (p. ej. tratamiento) " +
            "para una fila correspondiente a la pregunta 2.",
            { title: "Validación", variant: "warning", solid: true }
          );
          return;
        }
      }

      if (!this.validation()) {
        return;
      }

      this.$store
        .dispatch("wizard/save", { index: 4, form: this.form })
        .then(() => this.$router.push("/form/colombia-vida-4.html"));
    },
    tableEmpty() {
      for (let i = 0; i < this.form.padecimientos.length; i++) {
        if (!this.rowEmpty(i)) {
          return false;
        }
      }
      return true;
    },
    rowEmpty(index) {
      return (
        // this.form.padecimientos[index].pregunta == "" &&
        this.form.padecimientos[index].tratamiento == "" &&
        this.form.padecimientos[index].medicoHospital == "" &&
        this.form.padecimientos[index].fechaInicio == "" &&
        this.form.padecimientos[index].fechaFin == ""
      );
    },
    validation() {
      let ok = true;

      for (let i = 0; i < this.form.padecimientos.length; i++) {
        if (!this.rowEmpty(i)) {
          ok =
            this.getValidationPregunta(i) &&
            this.getValidationTratamiento(i) &&
            this.getValidationMedicoHospital(i) &&
            this.getValidationFechaInicio(i) &&
            this.getValidationFechaFin(i);
        }
      }
      return ok;
    },
    padecimientoConDatos(p) {
      return p.tratamiento.trim() != "" ||
        p.medicoHospital.trim() != "" ||
        p.fechaInicio.trim() != "" ||
        p.fechaFin.trim() != "";

    },
  }
};
</script>

<style scoped>
.container {
  max-width: 1600px;
}

.tabla-padecimientos td {
  padding: 0.35rem;
  vertical-align: middle;
}

/* Responsive: misma lógica que grilla beneficiarios (Form02) */
@media only screen and (max-width: 1000px) {

  .tabla-padecimientos,
  .tabla-padecimientos thead,
  .tabla-padecimientos tbody,
  .tabla-padecimientos th,
  .tabla-padecimientos td,
  .tabla-padecimientos tr {
    display: block;
    position: relative;
  }

  .tabla-padecimientos thead tr {
    display: none;
  }

  .tabla-padecimientos tbody tr {
    margin-bottom: 40px;
  }

  .tabla-padecimientos td {
    border-bottom: 1px solid #eee;
    width: 100% !important;
  }

  .tabla-padecimientos td:has(input, select) {
    padding-top: 30px;
  }

  .tabla-padecimientos td::before {
    position: absolute;
    top: 3px;
    left: 6px;
    padding-right: 10px;
    white-space: nowrap;
    font-weight: bold;
  }

  .tabla-padecimientos td:nth-of-type(1) {
    display: block !important;
    border-bottom: 2px solid var(--primary);
    margin-bottom: 20px;
  }

  .tabla-padecimientos td:nth-of-type(2)::before {
    content: "Corresponde a pregunta";
  }

  .tabla-padecimientos td:nth-of-type(3)::before {
    content: "Tratamiento / detalle";
  }

  .tabla-padecimientos td:nth-of-type(4)::before {
    content: "Médico / hospital";
  }

  .tabla-padecimientos td:nth-of-type(5)::before {
    content: "Fecha inicio";
  }

  .tabla-padecimientos td:nth-of-type(6)::before {
    content: "Fecha fin";
  }
}
</style>
